import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { Dayjs } from "dayjs";

import { useTreatmentCostAnalysis } from "../api/treatments";
import { TreatmentCostAnalysisItem } from "../types/treatment";
import { useUnitSystem } from "../hooks/useUnitSystem";
import { formatArea, formatDistance } from "../utils/units";

type TreatmentCostAnalysisModalProps = {
  open: boolean;
  onClose: () => void;
};

const formatNumber = (value: number | string | null | undefined) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return "0.00";
  }

  return numericValue.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatSize = (item: TreatmentCostAnalysisItem, unitSystem: "metric" | "imperial") => {
  const unit = (item.sizeUnit || "").toLowerCase();
  if (unit === "ha") {
    return formatArea(item.sizeValue, unitSystem);
  }
  if (unit === "km") {
    return formatDistance(item.sizeValue, unitSystem);
  }
  return `${formatNumber(item.sizeValue)} ${item.sizeUnit}`;
};

const formatDateLabel = (value: string) => dayjs(value).format("YYYY-MM-DD");

const csvEscape = (value: string | number | null) => {
  const raw = value === null || value === undefined ? "" : String(value);
  if (raw.includes("\"") || raw.includes(",") || raw.includes("\n")) {
    return `"${raw.replace(/"/g, '""')}"`;
  }
  return raw;
};

const buildCsv = (items: TreatmentCostAnalysisItem[], unitSystem: "metric" | "imperial") => {
  const header = ["Date", "Task Name", "Type", "Size", "Cost"].join(",");
  const rows = items.map((item) =>
    [
      csvEscape(item.date),
      csvEscape(item.name),
      csvEscape(item.type),
      csvEscape(formatSize(item, unitSystem)),
      csvEscape(formatNumber(item.cost)),
    ].join(",")
  );

  return [header, ...rows].join("\n");
};

export default function TreatmentCostAnalysisModal({
  open,
  onClose,
}: TreatmentCostAnalysisModalProps) {
  const unitSystem = useUnitSystem();
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [activePreset, setActivePreset] = useState<string | null>(null);

  const startDateParam = startDate?.format("YYYY-MM-DD") ?? null;
  const endDateParam = endDate?.format("YYYY-MM-DD") ?? null;

  const { data, isLoading, error } = useTreatmentCostAnalysis(
    {
      startDate: startDateParam,
      endDate: endDateParam,
    },
    open
  );

  const analysis = data?.data;

  const availableStartDays = useMemo(
    () =>
      (analysis?.availableStartDates ?? [])
        .map((date) => dayjs(date))
        .filter((value) => value.isValid())
        .sort((a, b) => a.valueOf() - b.valueOf()),
    [analysis?.availableStartDates]
  );

  const availableEndDays = useMemo(
    () =>
      (analysis?.availableEndDates ?? [])
        .map((date) => dayjs(date))
        .filter((value) => value.isValid())
        .sort((a, b) => a.valueOf() - b.valueOf()),
    [analysis?.availableEndDates]
  );

  useEffect(() => {
    if (!open) {
      setStartDate(null);
      setEndDate(null);
      setActivePreset(null);
    }
  }, [open]);

  useEffect(() => {
    if (!analysis || startDate) {
      return;
    }

    if (availableStartDays.length > 0) {
      setStartDate(availableStartDays[0]);
    }
  }, [analysis, startDate, availableStartDays]);

  useEffect(() => {
    if (!analysis || endDate) {
      return;
    }

    if (availableEndDays.length > 0) {
      const fallback = availableEndDays[availableEndDays.length - 1];
      setEndDate(fallback);
    }
  }, [analysis, endDate, availableEndDays]);

  useEffect(() => {
    if (!startDate || !endDate) {
      return;
    }

    if (endDate.isBefore(startDate)) {
      setEndDate(startDate);
    }
  }, [startDate, endDate]);

  const datePickerSlotProps = {
    textField: {
      size: "small" as const,
      sx: {
        minWidth: 200,
        my: 2,
        "& .MuiInputBase-root": {
          alignItems: "center",
          color: "white",
        },
        "& .MuiInputBase-input": {
          color: "white",
          py: 1,
        },
        "& .MuiInputLabel-root": {
          color: "rgba(255,255,255,0.7)",
        },
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "rgba(255,255,255,0.3)",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: "#00d4ad",
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: "#00d4ad",
        },
        "& .MuiSvgIcon-root": { color: "white" },
      },
    },
  };

  const items = analysis?.items ?? [];
  const totalCost = analysis?.totalCost ?? 0;

  type PresetOption = {
    id: string;
    label: string;
    start: Dayjs;
    end: Dayjs;
  };

  const presets = useMemo<PresetOption[]>(() => {
    if (!availableStartDays.length) {
      return [];
    }

    const min = availableStartDays[0];
    const max =
      availableEndDays.length > 0
        ? availableEndDays[availableEndDays.length - 1]
        : availableStartDays[availableStartDays.length - 1];

    if (!min || !max) {
      return [];
    }

    const clampToRange = (date: Dayjs) => {
      let value = date.startOf("day");
      if (value.isBefore(min)) {
        value = min;
      }
      if (value.isAfter(max)) {
        value = max;
      }
      return value;
    };

    const result: PresetOption[] = [];

    const pushIfValid = (option: PresetOption) => {
      if (!option.start.isAfter(option.end)) {
        result.push(option);
      }
    };

    pushIfValid({
      id: "full_range",
      label: "Entire range",
      start: clampToRange(min),
      end: clampToRange(max),
    });

    const today = dayjs();
    const lastTwelveMonthsEnd = clampToRange(today);
    const lastTwelveMonthsStart = clampToRange(today.subtract(1, "year"));
    pushIfValid({
      id: "last_12_months",
      label: "Last 12 months",
      start: lastTwelveMonthsStart,
      end: lastTwelveMonthsEnd,
    });

    const startOfCurrentYear = dayjs().startOf("year");
    const endOfToday = today.endOf("day");
    if (!startOfCurrentYear.isAfter(max) && !endOfToday.isBefore(min)) {
      const ytdStart = clampToRange(startOfCurrentYear);
      const ytdEnd = clampToRange(endOfToday.isAfter(max) ? max : endOfToday);
      pushIfValid({
        id: "year_to_date",
        label: `Year to date (${today.year()})`,
        start: ytdStart,
        end: ytdEnd,
      });
    }

    const availableYears = new Set<number>();
    availableStartDays.forEach((date) => availableYears.add(date.year()));
    availableEndDays.forEach((date) => availableYears.add(date.year()));

    Array.from(availableYears)
      .sort((a, b) => b - a)
      .forEach((year) => {
        const yearStart = clampToRange(dayjs().year(year).startOf("year"));
        const yearEnd = clampToRange(dayjs().year(year).endOf("year"));
        pushIfValid({
          id: `calendar_${year}`,
          label: `Calendar year ${year}`,
          start: yearStart,
          end: yearEnd,
        });
      });

    return result;
  }, [availableStartDays, availableEndDays]);

  useEffect(() => {
    if (!startDate || !endDate) {
      if (activePreset !== null) {
        setActivePreset(null);
      }
      return;
    }

    const matchingPreset = presets.find(
      (preset) =>
        startDate.isSame(preset.start, "day") && endDate.isSame(preset.end, "day")
    );

    if (matchingPreset) {
      if (activePreset !== matchingPreset.id) {
        setActivePreset(matchingPreset.id);
      }
    } else if (activePreset !== null) {
      setActivePreset(null);
    }
  }, [presets, startDate, endDate, activePreset]);

  const handlePresetSelect = (presetId: string) => {
    const preset = presets.find((option) => option.id === presetId);
    if (!preset) {
      return;
    }
    setActivePreset(presetId);
    setStartDate(preset.start.clone());
    setEndDate(preset.end.clone());
  };

  const handleExportCsv = () => {
    if (!items.length) {
      return;
    }

    const csv = buildCsv(items, unitSystem);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "mitigation_cost_analysis.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const content = () => {
    if (isLoading) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 240,
            gap: 2,
          }}
        >
          <CircularProgress sx={{ color: "#00d4ad" }} />
          <Typography>Loading cost analysis...</Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ my: 2 }}>
          Failed to load cost analysis.
        </Alert>
      );
    }

    if (!items.length) {
      return (
        <Alert severity="info" sx={{ my: 2 }}>
          No mitigation works scheduled for the selected period.
        </Alert>
      );
    }

    return (
      <TableContainer
        sx={{
          maxHeight: "calc(var(--vh100) * 0.5)",
          mt: 2,
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: 1,
        }}
      >
        <Table stickyHeader size="small" sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: "white", bgcolor: "#1a242f" }}>Date</TableCell>
              <TableCell sx={{ color: "white", bgcolor: "#1a242f" }}>Task Name</TableCell>
              <TableCell sx={{ color: "white", bgcolor: "#1a242f" }}>Type</TableCell>
              <TableCell sx={{ color: "white", bgcolor: "#1a242f" }}>Size</TableCell>
              <TableCell sx={{ color: "white", bgcolor: "#1a242f" }} align="right">
                Cost
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id} hover>
                <TableCell sx={{ color: "white" }}>
                  {item.date ? formatDateLabel(item.date) : "—"}
                </TableCell>
                <TableCell sx={{ color: "white" }}>{item.name ?? "—"}</TableCell>
                <TableCell sx={{ color: "white" }}>{item.type ?? "—"}</TableCell>
                <TableCell sx={{ color: "white" }}>{formatSize(item, unitSystem)}</TableCell>
                <TableCell sx={{ color: "white" }} align="right">
                  {`$${formatNumber(item.cost)}`}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "#212b37",
          color: "white",
          minHeight: "calc(var(--vh100) * 0.6)",
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: "bold" }}>
          Mitigation Strategy Cost Analysis
        </Typography>
      </DialogTitle>

      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <DatePicker
              label="Start Date"
              value={startDate}
              format="DD MMM YYYY"
              onChange={(value) => {
                setActivePreset(null);
                setStartDate(value ? value.startOf("day") : null);
              }}
              slotProps={datePickerSlotProps}
            />
            <DatePicker
              label="End Date"
              value={endDate}
              format="DD MMM YYYY"
              onChange={(value) => {
                setActivePreset(null);
                setEndDate(value ? value.startOf("day") : null);
              }}
              slotProps={datePickerSlotProps}
            />
          </Box>
        </LocalizationProvider>

        {presets.length > 0 && (
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography variant="subtitle2" sx={{ color: "rgba(255,255,255,0.7)" }}>
              Quick ranges
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {presets.map((preset) => {
                const isActive = activePreset === preset.id;
                return (
                  <Button
                    key={preset.id}
                    size="small"
                    variant={isActive ? "contained" : "outlined"}
                    onClick={() => handlePresetSelect(preset.id)}
                    sx={
                      isActive
                        ? {
                            bgcolor: "#00d4ad",
                            color: "#101b26",
                            borderColor: "#00d4ad",
                            "&:hover": {
                              bgcolor: "#00b89a",
                              borderColor: "#00b89a",
                            },
                          }
                        : {
                            borderColor: "rgba(255,255,255,0.3)",
                            color: "rgba(255,255,255,0.85)",
                            "&:hover": {
                              borderColor: "#00d4ad",
                              color: "#00d4ad",
                            },
                          }
                    }
                  >
                    {preset.label}
                  </Button>
                );
              })}
            </Box>
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 3,
          }}
        >
          <Typography variant="subtitle1" sx={{ color: "#00d4ad" }}>
            Total Cost: {`$${formatNumber(totalCost)}`}
          </Typography>
          <Button
            variant="outlined"
            onClick={handleExportCsv}
            disabled={!items.length}
            sx={{
              borderColor: "#00d4ad",
              color: "#00d4ad",
              "&.Mui-disabled": {
                borderColor: "rgba(255,255,255,0.3)",
                color: "rgba(255,255,255,0.3)",
              },
            }}
          >
            Export CSV
          </Button>
        </Box>

        {content()}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            color: "#00d4ad",
            borderColor: "#00d4ad",
            "&:hover": {
              borderColor: "#00b89a",
              bgcolor: "rgba(0, 212, 173, 0.1)",
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
